import {
  Video,
  S3Tags,
  TagHierarchy,
  TagFilters,
  ParsedTag,
  ParsedTagValue,
} from "../types/api";

/**
 * Parses a tag value that may contain multiple values separated by "+"
 */
export function parseTagValue(value: string): ParsedTagValue {
  const values = value
    .split("+")
    // convert first ":" to "(" and second ":" to ")"
    .map((v) => v.replace(":", "(").replace(":", ")"))
    // trim whitespace and filter out empty values
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  return {
    original: value,
    values: values,
  };
}

/**
 * Parses an S3 tag into a structured format
 */
export function parseTag(tag: S3Tags): ParsedTag {
  return {
    key: tag.Key,
    parsedValue: parseTagValue(tag.Value),
  };
}

function capitalizeForDisplay(word: string): string {
  // Handle special case for words starting with "("
  if (word.charAt(0) === "(" && word.length > 1) {
    return "(" + word.charAt(1).toUpperCase() + word.slice(2);
  }

  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Formats a tag value for display (converts underscores to spaces and capitalizes words)
 */
export function formatTagValueForDisplay(value: string): string {
  return value.split("_").map(capitalizeForDisplay).join(" ");
}

/**
 * Builds a tag hierarchy from all videos (excludes Groups tag)
 */
export function buildTagHierarchy(videos: Video[]): TagHierarchy {
  const hierarchy: TagHierarchy = {};

  videos.forEach((video) => {
    if (video.tags) {
      video.tags.forEach((tag) => {
        // Skip the Groups tag as it's used for backend filtering
        if (tag.Key.toLowerCase() === "groups") {
          return;
        }

        const parsedTag = parseTag(tag);

        if (!hierarchy[parsedTag.key]) {
          hierarchy[parsedTag.key] = [];
        }

        // Add all individual values to the hierarchy
        parsedTag.parsedValue.values.forEach((value) => {
          if (!hierarchy[parsedTag.key].includes(value)) {
            hierarchy[parsedTag.key].push(value);
          }
        });
      });
    }
  });

  // Sort values within each tag key
  Object.keys(hierarchy).forEach((key) => {
    hierarchy[key].sort();
  });

  return hierarchy;
}

/**
 * Checks if a video matches the selected tag filters
 */
export function matchesTagFilters(
  video: Video,
  tagFilters: TagFilters,
): boolean {
  // If no filters are selected, show all videos
  const hasActiveFilters = Object.values(tagFilters).some(
    (filterSet) => filterSet.size > 0,
  );
  if (!hasActiveFilters) {
    return true;
  }

  // For each tag key with active filters, check if video has matching values
  for (const [tagKey, selectedValues] of Object.entries(tagFilters)) {
    if (selectedValues.size === 0) continue; // Skip empty filters

    // Find the video's tag with this key
    const videoTag = video.tags?.find((tag) => tag.Key === tagKey);
    if (!videoTag) return false; // Video doesn't have this tag key

    // Parse the video's tag values
    const parsedTag = parseTag(videoTag);

    // Check if any of the video's values match the selected filters
    const hasMatchingValue = parsedTag.parsedValue.values.some((value) =>
      selectedValues.has(value),
    );

    if (!hasMatchingValue) return false; // This tag key has no matching values
  }

  return true; // All active filters have matches
}

/**
 * Filters videos based on tag selections
 */
export function filterVideosByTags(
  videos: Video[],
  tagFilters: TagFilters,
): Video[] {
  return videos.filter((video) => matchesTagFilters(video, tagFilters));
}
